// A stable key for the dataset/subset data container a route belongs to. Used
// to decide when an in-flight long-running poll should be aborted: only a key
// change (different dataset, or different item/subset) cancels the poll —
// going DEEPER within the same container (opening a form, filter, export,
// reference, or row detail) keeps the same key, so the poll survives.
//
// Examples (same key → no abort):
//   /dataset/A                          -> "A"
//   /dataset/A/filter                   -> "A"
//   /dataset/A/form/F/add               -> "A"   (form opened from the list)
//   /dataset/A/detail/X                 -> "A"   (row detail, no subset)
//   /dataset/A/item/I/subset/S          -> "A|I|S"
//   /dataset/A/item/I/subset/S/form/..  -> "A|I|S" (form opened from the subset)
//   /dataset/A/detail/I/subset/S        -> "A|I|S" (subset under fullscreen detail)
// Different key → abort:
//   /dataset/A  ->  /dataset/B                  ("A"   -> "B")
//   /dataset/A/item/I/subset/S1 -> .../subset/S2 ("A|I|S1" -> "A|I|S2")
//   .../detail/I/subset/S1      -> .../subset/S2 (subset switch inside detail)
export const getPollScopeKey = (pathname: string): string => {
  const datasetMatch = pathname.match(/\/dataset\/([^/]+)/);

  // Non-dataset pages (login, dashboards, standalone forms, ...) keyed by the
  // full path so leaving them still aborts any leftover poll.
  if (!datasetMatch) {
    return pathname;
  }

  const datasetId = datasetMatch[1];
  // Subset lives under both the in-grid path (/item/:id/subset/:id) and the
  // fullscreen detail path (/detail/:id/subset/:id) — switching subsets in
  // either must change the key so the left subset's poll is aborted.
  const subsetMatch = pathname.match(
    /\/(?:item|detail)\/([^/]+)\/subset\/([^/]+)/
  );

  if (subsetMatch) {
    return `${datasetId}|${subsetMatch[1]}|${subsetMatch[2]}`;
  }

  return datasetId;
};
